import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Avatar,
  Button,
  Chip,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
} from '@mui/material';
import {
  ArrowBack,
  Delete,
  CheckCircle,
  PendingActions,
  Person,
  Description,
  CalendarToday,
  Warning,
  Block,
  Info,
} from '@mui/icons-material';
import adminService from '../../../services/admin.service';

interface ReportDetails {
  reportId: string;
  reporterId: string;
  reporterFullname: string;
  reporterProfileImage: string;
  reporterSchoolName: string;
  reportedUserId: string;
  reportedUserFullname: string;
  reportedUserProfileImage: string;
  reportedUserSchoolName: string;
  reportType: string;
  reportDescription: string;
  reportData: any;
  isResolved: boolean;
  resolvedAt: string | null;
  resolvedBy: string | null;
  createdAt: string;
  updatedAt: string;
}

const ReportDetail: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();

  const [report, setReport] = useState<ReportDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Dialogs
  const [resolveDialog, setResolveDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [action, setAction] = useState('NO_ACTION');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (reportId) {
      fetchReportDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  const fetchReportDetails = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminService.getReportDetail(reportId!);

      if (response.status) {
        setReport(response.data);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Rapor bilgileri yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleResolveReport = async () => {
    if (!note.trim()) {
      alert('Lütfen bir not ekleyin.');
      return;
    }

    setActionLoading(true);
    try {
      await adminService.resolveReport(reportId!, { action, note });
      setResolveDialog(false);
      setNote('');
      alert('Rapor başarıyla çözüme ulaştırıldı!');
      await fetchReportDetails();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Rapor çözümlenirken hata oluştu.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteReport = async () => {
    setActionLoading(true);
    try {
      await adminService.deleteReport(reportId!);
      alert('Rapor başarıyla silindi!');
      navigate('/admin/reports');
    } catch (err: any) {
      alert(err.response?.data?.message || 'Rapor silinirken hata oluştu.');
    } finally {
      setActionLoading(false);
      setDeleteDialog(false);
    }
  };

  const getReportTypeLabel = (type: string) => {
    const types: { [key: string]: string } = {
      USER: 'Kullanıcı',
      POST: 'Gönderi',
      MESSAGE: 'Mesaj',
      COMMENT: 'Yorum',
      SUB_COMMENT: 'Alt Yorum',
      SPAM: 'Spam',
      GENERAL: 'Genel',
      OTHER: 'Diğer',
    };
    return types[type] || type;
  };

  const getReportTypeColor = (type: string): 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' => {
    const colors: { [key: string]: 'default' | 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' } = {
      USER: 'error',
      POST: 'warning',
      MESSAGE: 'info',
      COMMENT: 'primary',
      SUB_COMMENT: 'primary',
      SPAM: 'error',
      GENERAL: 'default',
      OTHER: 'default',
    };
    return colors[type] || 'default';
  };

  const getActionLabel = (actionType: string) => {
    const actions: { [key: string]: string } = {
      NO_ACTION: 'İşlem Yapma',
      WARN_USER: 'Kullanıcıyı Uyar',
      BAN_USER: 'Kullanıcıyı Banla',
    };
    return actions[actionType] || actionType;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !report) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Rapor bulunamadı.'}</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/admin/reports')}
          sx={{ mt: 2 }}
        >
          Geri Dön
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <IconButton onClick={() => navigate('/admin/reports')}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight="bold">
            Rapor Detayı
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Rapor ID: {report.reportId}
          </Typography>
        </Box>
        {report.isResolved ? (
          <Chip
            icon={<CheckCircle />}
            label="Çözümlendi"
            color="success"
            size="medium"
          />
        ) : (
          <Chip
            icon={<PendingActions />}
            label="Beklemede"
            color="warning"
            size="medium"
          />
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Sol Kolon - Rapor Bilgileri */}
        <Grid item xs={12} md={8}>
          {/* Rapor Tipi ve Açıklama */}
          <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Description color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Rapor Bilgileri
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Rapor Tipi
              </Typography>
              <Chip
                label={getReportTypeLabel(report.reportType)}
                color={getReportTypeColor(report.reportType)}
                size="medium"
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Rapor Açıklaması
              </Typography>
              <Typography variant="body1">
                {report.reportDescription}
              </Typography>
            </Box>

            {report.reportData && Object.keys(report.reportData).length > 0 && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Ek Bilgiler
                </Typography>
                <Paper
                  elevation={0}
                  sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}
                >
                  <pre style={{ margin: 0, fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    {JSON.stringify(report.reportData, null, 2)}
                  </pre>
                </Paper>
              </Box>
            )}
          </Paper>

          {/* Raporlayan Kullanıcı */}
          <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Person color="primary" />
              <Typography variant="h6" fontWeight="bold">
                Raporlayan Kullanıcı
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={report.reporterProfileImage || ''}
                alt={report.reporterFullname}
                sx={{ width: 64, height: 64 }}
              >
                {report.reporterFullname?.[0]?.toUpperCase() || 'U'}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight="600">
                  {report.reporterFullname}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {report.reporterSchoolName || 'Okul bilgisi yok'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ID: {report.reporterId}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={() => navigate(`/admin/users/${report.reporterId}`)}
              >
                Profili Görüntüle
              </Button>
            </Box>
          </Paper>

          {/* Raporlanan Kullanıcı */}
          <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <Warning color="error" />
              <Typography variant="h6" fontWeight="bold">
                Raporlanan Kullanıcı
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={report.reportedUserProfileImage || ''}
                alt={report.reportedUserFullname}
                sx={{ width: 64, height: 64 }}
              >
                {report.reportedUserFullname?.[0]?.toUpperCase() || 'U'}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" fontWeight="600">
                  {report.reportedUserFullname}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {report.reportedUserSchoolName || 'Okul bilgisi yok'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ID: {report.reportedUserId}
                </Typography>
              </Box>
              <Button
                variant="outlined"
                onClick={() => navigate(`/admin/users/${report.reportedUserId}`)}
              >
                Profili Görüntüle
              </Button>
            </Box>
          </Paper>

          {/* Çözüm Bilgileri (eğer çözüldüyse) */}
          {report.isResolved && report.reportData?.adminAction && (
            <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: 3, border: '1px solid #e0e0e0', backgroundColor: '#f0f9ff' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Info color="info" />
                <Typography variant="h6" fontWeight="bold">
                  Çözüm Bilgileri
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Alınan Aksiyon
                </Typography>
                <Chip
                  label={getActionLabel(report.reportData.adminAction)}
                  color={
                    report.reportData.adminAction === 'BAN_USER'
                      ? 'error'
                      : report.reportData.adminAction === 'WARN_USER'
                      ? 'warning'
                      : 'default'
                  }
                  size="medium"
                />
              </Box>

              {report.reportData.adminNote && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Admin Notu
                  </Typography>
                  <Typography variant="body1">
                    {report.reportData.adminNote}
                  </Typography>
                </Box>
              )}

              {report.resolvedAt && (
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Çözüm Tarihi
                  </Typography>
                  <Typography variant="body2">
                    {new Date(report.resolvedAt).toLocaleString('tr-TR')}
                  </Typography>
                </Box>
              )}
            </Paper>
          )}
        </Grid>

        {/* Sağ Kolon - İşlemler */}
        <Grid item xs={12} md={4}>
          {/* Tarih Bilgileri */}
          <Card sx={{ mb: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <CalendarToday color="action" fontSize="small" />
                <Typography variant="subtitle2" fontWeight="600">
                  Tarih Bilgileri
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Oluşturulma Tarihi
                </Typography>
                <Typography variant="body2">
                  {new Date(report.createdAt).toLocaleString('tr-TR')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Son Güncelleme
                </Typography>
                <Typography variant="body2">
                  {new Date(report.updatedAt).toLocaleString('tr-TR')}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* İşlemler */}
          {!report.isResolved && (
            <Card sx={{ mb: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
              <CardContent>
                <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                  İşlemler
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Button
                  fullWidth
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={() => setResolveDialog(true)}
                  sx={{ mb: 1 }}
                >
                  Raporu Çözüme Ulaştır
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setDeleteDialog(true)}
                >
                  Raporu Sil
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Hızlı Erişim */}
          <Card sx={{ borderRadius: 3, border: '1px solid #e0e0e0' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                Hızlı Erişim
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate(`/admin/reports/user/${report.reportedUserId}`)}
                sx={{ mb: 1 }}
              >
                Kullanıcının Diğer Raporları
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => navigate('/admin/reports')}
              >
                Tüm Raporlar
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Çözüm Dialogu */}
      <Dialog open={resolveDialog} onClose={() => setResolveDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Raporu Çözüme Ulaştır</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Alınacak Aksiyon</InputLabel>
              <Select
                value={action}
                label="Alınacak Aksiyon"
                onChange={(e) => setAction(e.target.value)}
              >
                <MenuItem value="NO_ACTION">İşlem Yapma (Rapor Geçersiz)</MenuItem>
                <MenuItem value="WARN_USER">Kullanıcıyı Uyar</MenuItem>
                <MenuItem value="BAN_USER">Kullanıcıyı Banla</MenuItem>
              </Select>
            </FormControl>

            <Alert severity="info" sx={{ mb: 3 }}>
              {action === 'NO_ACTION' && 'Rapor kapatılacak, kullanıcıya bir şey olmayacak.'}
              {action === 'WARN_USER' && 'Raporlanan kullanıcıya otomatik push notification gönderilecek.'}
              {action === 'BAN_USER' && 'Kullanıcı otomatik olarak banlanacak!'}
            </Alert>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="Admin Notu"
              placeholder="Bu karar hakkında not ekleyin (zorunlu)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveDialog(false)} disabled={actionLoading}>
            İptal
          </Button>
          <Button
            onClick={handleResolveReport}
            variant="contained"
            color="success"
            disabled={actionLoading || !note.trim()}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Onayla'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Silme Dialogu */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Raporu Sil</DialogTitle>
        <DialogContent>
          <Typography>
            Bu raporu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)} disabled={actionLoading}>
            İptal
          </Button>
          <Button onClick={handleDeleteReport} color="error" disabled={actionLoading}>
            {actionLoading ? <CircularProgress size={24} /> : 'Sil'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ReportDetail;

